import { t as createLogger } from "./app.shared-CmP4XLF_.js";

var log = createLogger("custom-select");

var custom_select_default = (() => {
    if (customElements.get("custom-select")) return;
    customElements.define("custom-select", class CustomSelect extends HTMLElement {
        connectedCallback() {
            this.trigger = this.querySelector("[data-select-trigger]");
            this.panel = this.querySelector("[data-select-panel]");
            this.valueEl = this.querySelector("[data-select-value]");
            this.labelEl = this.querySelector("[data-select-label]");
            this.options = Array.from(this.querySelectorAll("[data-select-option]"));
            if (!this.trigger || !this.panel || !this.options.length) return;
            this.name = this.dataset.name || "";
            this.open = false;
            this.activeIndex = -1;
            this.wireAria();
            this.reflectSelected();
            this.trigger.addEventListener("click", this.onTriggerClick);
            this.options.forEach(opt => opt.addEventListener("click", this.onOptionClick));
            this.trigger.addEventListener("keydown", this.onKeyDown);
            this.panel.addEventListener("keydown", this.onPanelKeyDown);
            this.addEventListener("focusout", this.onFocusOut);
            document.addEventListener("click", this.onDocumentClick, true);
        }
        disconnectedCallback() {
            this.trigger?.removeEventListener("click", this.onTriggerClick);
            this.options.forEach(opt => opt.removeEventListener("click", this.onOptionClick));
            this.trigger?.removeEventListener("keydown", this.onKeyDown);
            this.panel?.removeEventListener("keydown", this.onPanelKeyDown);
            this.removeEventListener("focusout", this.onFocusOut);
            document.removeEventListener("click", this.onDocumentClick, true);
            cancelAnimationFrame(this.focusCheckRaf);
        }
        wireAria() {
            const uid = `cs-${Math.random().toString(36).slice(2, 9)}`;
            this.trigger.setAttribute("role", "combobox");
            this.trigger.setAttribute("aria-haspopup", "listbox");
            this.trigger.setAttribute("aria-expanded", "false");
            if (!this.trigger.id) this.trigger.id = `${uid}-trigger`;
            this.panel.id = `${uid}-listbox`;
            this.panel.setAttribute("role", "listbox");
            this.trigger.setAttribute("aria-controls", this.panel.id);
            if (this.labelEl) {
                this.labelEl.id = `${uid}-label`;
                this.panel.setAttribute("aria-labelledby", this.labelEl.id);
                this.trigger.setAttribute("aria-labelledby", this.labelEl.id);
            }
            this.options.forEach((opt, i) => {
                opt.setAttribute("role", "option");
                opt.id = `${uid}-option-${i}`;
                opt.tabIndex = -1;
                if (!opt.hasAttribute("aria-selected")) opt.setAttribute("aria-selected", "false");
            });
        }
        reflectSelected() {
            const idx = this.options.findIndex(o => o.getAttribute("aria-selected") === "true");
            if (idx < 0) return;
            this.activeIndex = idx;
            this.trigger.setAttribute("aria-activedescendant", this.options[idx].id);
            if (this.valueEl) this.valueEl.textContent = this.options[idx].textContent;
        }
        setOpen(next) {
            if (next === this.open) return;
            this.open = next;
            this.toggleAttribute("data-open", next);
            this.trigger.setAttribute("aria-expanded", String(next));
            if (next) {
                this.panel.inert = false;
                if (this.activeIndex < 0) this.activeIndex = this.firstIndex();
                this.highlight(this.activeIndex);
            } else {
                this.panel.inert = true;
                this.clearHighlight();
                const selected = this.options.findIndex(o => o.getAttribute("aria-selected") === "true");
                this.activeIndex = selected;
                if (selected >= 0) this.trigger.setAttribute("aria-activedescendant", this.options[selected].id); else this.trigger.removeAttribute("aria-activedescendant");
            }
        }
        setSelected(value) {
            const idx = this.options.findIndex(o => o.dataset.selectOption === value);
            if (idx < 0) {
                log("errors", "setSelected no-op — option not found", {
                    name: this.name,
                    value: value,
                    renderedOptions: this.options.map(o => o.dataset.selectOption)
                });
                return;
            }
            this.markSelected(idx);
        }
        setAvailability(oosValues = []) {
            this.options.forEach(opt => {
                const oos = oosValues.includes(opt.dataset.selectOption);
                opt.toggleAttribute("aria-out-of-stock", oos);
                opt.classList.toggle("opacity-30", oos);
            });
        }
        markSelected(index) {
            const opt = this.options[index];
            if (!opt) return;
            this.options.forEach(o => o.setAttribute("aria-selected", "false"));
            opt.setAttribute("aria-selected", "true");
            this.activeIndex = index;
            if (this.valueEl) this.valueEl.textContent = opt.textContent;
            this.trigger.setAttribute("aria-activedescendant", opt.id);
        }
        commit(index) {
            const opt = this.options[index];
            if (!opt) return false;
            this.markSelected(index);
            this.dispatchEvent(new CustomEvent("select:change", {
                bubbles: true,
                detail: {
                    value: opt.dataset.selectOption,
                    name: this.name
                }
            }));
            return true;
        }
        highlight(index) {
            this.clearHighlight();
            const opt = this.options[index];
            if (!opt) return;
            this.activeIndex = index;
            opt.setAttribute("data-active", "");
            opt.tabIndex = 0;
            this.trigger.setAttribute("aria-activedescendant", opt.id);
        }
        clearHighlight() {
            this.options.forEach(o => {
                o.removeAttribute("data-active");
                o.tabIndex = -1;
            });
        }
        firstIndex() {
            return this.options.length ? 0 : -1;
        }
        lastIndex() {
            return this.options.length - 1;
        }
        moveActive(step) {
            if (!this.options.length) return;
            const n = this.options.length;
            this.highlight((this.activeIndex + step + n) % n);
        }
        onTriggerClick=() => {
            this.setOpen(!this.open);
        };
        onOptionClick=event => {
            const index = this.options.indexOf(event.currentTarget);
            if (this.commit(index)) this.setOpen(false);
            this.trigger.focus({
                preventScroll: true
            });
        };
        onDocumentClick=event => {
            if (!this.open) return;
            if (!event.composedPath().includes(this)) this.setOpen(false);
        };
        onFocusOut=event => {
            if (!this.open) return;
            const next = event.relatedTarget;
            if (next && this.contains(next)) return;
            cancelAnimationFrame(this.focusCheckRaf);
            this.focusCheckRaf = requestAnimationFrame(() => {
                if (this.open && !this.contains(document.activeElement)) this.setOpen(false);
            });
        };
        onPanelKeyDown=event => {
            const {key: key} = event;
            switch (key) {
              case "ArrowDown":
                event.preventDefault();
                this.moveActive(1);
                this.options[this.activeIndex]?.focus();
                break;

              case "ArrowUp":
                event.preventDefault();
                this.moveActive(-1);
                this.options[this.activeIndex]?.focus();
                break;

              case "Home":
                event.preventDefault();
                this.highlight(this.firstIndex());
                this.options[this.activeIndex]?.focus();
                break;

              case "End":
                event.preventDefault();
                this.highlight(this.lastIndex());
                this.options[this.activeIndex]?.focus();
                break;

              case "Enter":
              case " ":
                {
                    if (event.target instanceof HTMLButtonElement) break;
                    event.preventDefault();
                    const index = this.options.indexOf(event.target);
                    if (this.commit(index < 0 ? this.activeIndex : index)) {
                        this.trigger.focus({
                            preventScroll: true
                        });
                        this.setOpen(false);
                    }
                    break;
                }

              case "Escape":
                event.preventDefault();
                this.trigger.focus({
                    preventScroll: true
                });
                this.setOpen(false);
                break;

              default:
                break;
            }
        };
        onKeyDown=event => {
            const {key: key} = event;
            if (!this.open && (key === "ArrowDown" || key === "ArrowUp" || key === "Enter" || key === " ")) {
                event.preventDefault();
                this.setOpen(true);
                if (key === "ArrowUp") this.highlight(this.lastIndex()); else if (this.activeIndex < 0) this.highlight(this.firstIndex());
                return;
            }
            switch (key) {
              case "ArrowDown":
                event.preventDefault();
                this.moveActive(1);
                break;

              case "ArrowUp":
                event.preventDefault();
                this.moveActive(-1);
                break;

              case "Home":
                event.preventDefault();
                this.highlight(this.firstIndex());
                break;

              case "End":
                event.preventDefault();
                this.highlight(this.lastIndex());
                break;

              case "Enter":
              case " ":
                event.preventDefault();
                if (this.commit(this.activeIndex)) this.setOpen(false);
                break;

              case "Escape":
                if (this.open) {
                    event.preventDefault();
                    this.setOpen(false);
                }
                break;

              default:
                break;
            }
        };
    });
})();